'use client';

import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { HostMode, HostType } from '@repo/types/host';
import type React from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import { Analytics, type AnalyticsConfig } from './analytics';
const AnalyticsContext = createContext<Analytics | null>(null);
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};

/**
 * For shared components that can also render outside an AnalyticsProvider (e.g. the site header on a
 * `global-error.tsx` page, which replaces the root layout), where `useAnalytics` would throw.
 */
export const useOptionalAnalytics = () => {
  return useContext(AnalyticsContext);
};
type AnalyticsProviderProps = {
  children: React.ReactNode;
  config: AnalyticsConfig;
};
export const AnalyticsProvider = ({
  children,
  config
}: AnalyticsProviderProps) => {
  const hostType = useHostType();
  const hostMode = useHostMode();
  const isEmbed = hostType === HostType.WEB && hostMode === HostMode.EMBED;
  const analyticsRef = useRef<Analytics | null>(null);
  if (analyticsRef.current === null) {
    analyticsRef.current = new Analytics({
      ...config,
      hostType,
      disableGA: config.disableGA || isEmbed
    });
  }
  const analytics = analyticsRef.current;
  useEffect(() => {
    analytics.initialize();
  }, [analytics]);
  useEffect(() => {
    analytics.reconfigure({
      disableGA: config.disableGA || isEmbed,
      userId: config.account?.uuid ?? config.userId ?? null
    });
  }, [analytics, config.account?.uuid, config.disableGA, config.userId, isEmbed]);
  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};